/**
 * ColorSection.tsx
 * 背景色 (bg_color), 文字色 (text_color)
 * Color input + text field with HEX validation
 */
import React from 'react';
import { SectionProps } from '../types';
import SectionHeader from '../shared/SectionHeader';
import { FieldMarkWrap } from '../conflict/fieldState';

const BG_FIELDS = ['bg_color'] as const;
const TEXT_FIELDS = ['text_color'] as const;

const HEX_PATTERN = /^#[0-9A-Fa-f]{6}$/;

const normalizeHex = (value: string): string => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (trimmed === '') return '';
  if (HEX_PATTERN.test(trimmed)) return trimmed.toUpperCase();
  const raw = trimmed.startsWith('#') ? trimmed.slice(1) : trimmed;
  if (/^[0-9A-Fa-f]{6}$/.test(raw)) return `#${raw.toUpperCase()}`;
  return trimmed;
};

const resolvePickerValue = (value: string, fallback: string): string =>
  HEX_PATTERN.test(value) ? value.toUpperCase() : fallback;

export default function ColorSection({
  data,
  onChange,
  disabled,
}: SectionProps) {
  const bgPickerVal = resolvePickerValue(data.bg_color, '#0ea5e9');
  const textPickerVal = resolvePickerValue(data.text_color, '#0f172a');

  const rowStyle: React.CSSProperties = { display: 'flex', gap: 8, alignItems: 'center' };
  const pickerStyle: React.CSSProperties = {
    width: 42,
    height: 32,
    padding: 0,
    border: '1px solid #cbd5e1',
    borderRadius: 6,
    background: '#fff',
    cursor: disabled ? 'default' : 'pointer',
  };

  return (
    <>
      <SectionHeader title="表示色" />
      <FieldMarkWrap fields={BG_FIELDS} className="gc-field">
        <label className="gc-label" style={{ fontSize: '0.85rem', color: '#64748b' }}>背景色</label>
        <div style={rowStyle}>
          <input
            type="text"
            className="gc-input"
            value={data.bg_color || ''}
            onChange={(e) => onChange?.({ bg_color: e.target.value })}
            onBlur={(e) => onChange?.({ bg_color: normalizeHex(e.target.value) })}
            disabled={disabled}
            placeholder="#000000"
            style={{ flex: 1 }}
          />
          <input
            type="color"
            value={bgPickerVal}
            onChange={(e) => onChange?.({ bg_color: e.target.value })}
            disabled={disabled}
            style={pickerStyle}
          />
        </div>
      </FieldMarkWrap>
      <FieldMarkWrap fields={TEXT_FIELDS} className="gc-field">
        <label className="gc-label" style={{ fontSize: '0.85rem', color: '#64748b' }}>文字色</label>
        <div style={rowStyle}>
          <input
            type="text"
            className="gc-input"
            value={data.text_color || ''}
            onChange={(e) => onChange?.({ text_color: e.target.value })}
            onBlur={(e) => onChange?.({ text_color: normalizeHex(e.target.value) })}
            disabled={disabled}
            placeholder="#000000"
            style={{ flex: 1 }}
          />
          <input
            type="color"
            value={textPickerVal}
            onChange={(e) => onChange?.({ text_color: e.target.value })}
            disabled={disabled}
            style={pickerStyle}
          />
        </div>
      </FieldMarkWrap>
    </>
  );
}
